import { Send as SendIcon, Transform as TransformIcon } from '@mui/icons-material';
import classnames from 'classnames';
import { RESOURCE_TYPE_DATA_SET, RESOURCE_TYPE_SINK } from 'js/constants/resourceTypes';
import LocalStorageUtils from 'js/lib/LocalStorageUtils';
import {
  isCapsuleCluster,
  isSingleChainFlow,
  isSingleDestinationChainFlow,
} from 'js/lib/PipelineUtils';
import { isFullScreenCanvasURL } from 'js/lib/URLUtils';
import flowTypes from 'new/components/flows/types/flowTypes';
import { ReactComponent as ActivateIcon } from 'new/components/icons/bolt.svg';
import { ReactComponent as CopyIcon } from 'new/components/icons/copy.svg';
import { ReactComponent as DuplicateIcon } from 'new/components/icons/copyAlt.svg';
import { ReactComponent as EditFileIcon } from 'new/components/icons/editFile.svg';
import { ReactComponent as DetailsIcon } from 'new/components/icons/fi-rr-info.svg';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { ReactComponent as MarketplaceIcon } from 'new/components/icons/icon-marketplace-alt.svg';
import { ReactComponent as PauseIcon } from 'new/components/icons/pause.svg';
import { editModalsEnabled } from 'new/constants/featureFlags';
import { ROUTE_CONNECT, ROUTE_ENABLE_MARKETPLACE } from 'new/constants/pathnames';
import { DetailsIconButton } from 'new/elements/Button';
import { getAvailableActions } from 'new/utils/nexsetActionRules';
import { isMarketplaceEligible } from 'new/utils/nexsetUtils';
import { createActionValidator, createDeleteActionValidator } from 'js/lib/flowValidationUtils';
import React from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import DataSetSplitDialog from '../../../../new/components/SplitDataSet/DataSetSplitDialog';
import RoutePrefixContext from '../../../../new/contexts/RoutePrefixContext';
import Menu from '../../../components/elements/menu/Menu';
import Constants from '../../../Constants';
import { ROUTE_MY_DATA_SETS, ROUTE_PIPELINES, ROUTE_REDESIGN } from '../../../constants/pathnames';
import {
  PIPELINE_ACTION_ACTIVATE,
  PIPELINE_ACTION_DELETE,
  PIPELINE_ACTION_PAUSE,
  DELETE_DISABLED_MESSAGES,
} from '../../../constants/pipelineConstants';
import { restrictMenuForRole } from '../../../lib/ACLUtils';
import Tooltip from '../../elements/mui/Tooltip';
import s from './DataSetDetailActions.module.scss';
import withModals from './withModals';

const ResourceEditProvider = React.lazy(
  () => import('js/components/ResourceEditProvider/ResourceEditProvider'),
);

import { ResourceEditContext } from 'js/components/ResourceEditProvider/ResourceEditContext';

const styles = {
  default: {
    cta: {
      fontSize: 20,
      color: 'var(--color-primary-500)',
      viewBox: '0 0 30 30',
      cursor: 'pointer',
    },
    ctaContainer: {
      marginLeft: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  detailPanel: {
    cta: {
      height: 30,
      fontSize: 20,
      color: 'black',
      viewBox: '0 0 30 30',
      cursor: 'pointer',
    },
    ctaContainer: {
      marginLeft: 12,
      marginRight: 3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  pipeline: {
    cta: {
      fontSize: 20,
      color: 'var(--color-primary-500)',
      viewBox: '0 0 18 18',
      cursor: 'pointer',
    },
    ctaContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 4,
    },
  },
};

export class DataSetDetailActions extends React.Component {
  static contextType = RoutePrefixContext;

  constructor(props) {
    super(props);

    this.state = {
      isSplitDialogOpen: false,
    };

    this._handleSendClicked = this._handleSendClicked.bind(this);
    this._handleDeleteClicked = this._handleDeleteClicked.bind(this);

    this._handlePauseClicked = this._handlePauseClicked.bind(this);
    this._handleActivateClicked = this._handleActivateClicked.bind(this);
    this._handleEditClicked = this._handleEditClicked.bind(this);
    this._handleTransformClicked = this._handleTransformClicked.bind(this);
    this._handleSplitClicked = this._handleSplitClicked.bind(this);
    this._handleCloseSplitDialog = this._handleCloseSplitDialog.bind(this);
    this._handleCopyClicked = this._handleCopyClicked.bind(this);
    this._handleCopyTransformClicked = this._handleCopyTransformClicked.bind(this);
    this._handlePasteTransformClicked = this._handlePasteTransformClicked.bind(this);
    this._handlePausePipelineClicked = this._handlePausePipelineClicked.bind(this);
    this._handleActivatePipelineClicked = this._handleActivatePipelineClicked.bind(this);
    this._handleDeletePipelineClicked = this._handleDeletePipelineClicked.bind(this);
    this._handleAddToMarketplace = this._handleAddToMarketplace.bind(this);
    this._handleOpenDetails = this._handleOpenDetails.bind(this);
  }

  getMenuItemDefinitions() {
    const {
      onEditButtonClicked,
      isPausing,
      isActivating,
      isCopying,
      isDeleting,
      clipboardDatasetId,
      disableCopying,
      disableEditMessage,
      data,
      isDetected,
      isManaged,
    } = this.props;

    // Status reflects the Nexset itself, not the upstream source. Pause /
    // activate from the Nexset card operate on the dataset only (a webhook
    // source feeding multiple Nexsets keeps ingesting when one Nexset is
    // paused), so deriving status from source.status would desync the UI
    // (wrong status badge, wrong Pause/Activate menu state, delete stuck
    // disabled) after a Nexset-scoped pause.
    const status = data.status;
    const isCapsule = isCapsuleCluster(data);

    const isEditable = !isDetected && !isManaged && (isCapsule || status !== 'ACTIVE');
    const editDisableMessage = isDetected
      ? 'Detected Nexsets cannot be edited'
      : isManaged
        ? 'This Nexset is managed by Nexla. Please contact support@nexla.com for editing this Nexset.'
        : !isCapsule && status === 'ACTIVE'
          ? 'Active resources cannot be edited. Please Pause to edit'
          : disableEditMessage;

    const resource = this.props?.flow ?? data;
    const actionsValidationForFlow = createActionValidator(resource, isPausing, isActivating);

    return {
      details: {
        label: 'Details',
        icon: <DetailsIcon />,
        onClick: this._handleOpenDetails,
      },
      edit: {
        label: 'Edit',
        icon: <EditFileIcon />,
        onClick: onEditButtonClicked || this._handleEditClicked,
        disable: !isEditable,
        disableMessage: editDisableMessage,
      },
      duplicate: {
        label: isCopying ? 'Duplicating...' : 'Duplicate',
        icon: <DuplicateIcon />,
        onClick: this._handleCopyClicked,
        disable: isCopying || isDetected || disableCopying,
        disableMessage: isDetected
          ? 'Detected Nexsets cannot be duplicated'
          : 'This Nexset cannot be duplicated',
      },
      delete: {
        type: 'dialog',
        label: isDeleting ? 'Deleting...' : 'Delete',
        icon: <DeleteIcon />,
        color: 'error',
        dialogMessage: `Please confirm you want to delete Nexset ${data.name} (ID: ${data.id})`,
        primaryButtonColor: 'error',
        discardLabel: 'Cancel',
        confirmLabel: 'Delete',
        onClick: this._handleDeleteClicked,
        ...createDeleteActionValidator(
          status,
          isDeleting,
          isPausing,
          DELETE_DISABLED_MESSAGES.NEXSET,
        ),
      },
      pause: {
        label: isPausing ? 'Pausing...' : 'Pause',
        icon: <PauseIcon />,
        onClick: this._handlePauseClicked,
        ...actionsValidationForFlow,
      },
      activate: {
        label: isActivating ? 'Activating...' : 'Activate',
        icon: <ActivateIcon />,
        disable: isActivating,
        onClick: this._handleActivateClicked,
        ...actionsValidationForFlow,
      },
      copyTransform: {
        label: 'Copy Transform',
        icon: <CopyIcon />,
        onClick: this._handleCopyTransformClicked,
      },
      pasteTransform: {
        label: 'Paste Transform',
        icon: <CopyIcon />,
        disable: !clipboardDatasetId,
        disableMessage: 'Copy a Transform to enable Paste',
        onClick: this._handlePasteTransformClicked,
      },
      addMarketplace: {
        label: 'Add to Marketplace',
        icon: <MarketplaceIcon />,
        onClick: this._handleAddToMarketplace,
      },
      pauseFlow: {
        label: 'Pause Flow',
        icon: <PauseIcon />,
        onClick: this._handlePausePipelineClicked,
        ...actionsValidationForFlow,
      },
      activateFlow: {
        label: 'Activate Flow',
        icon: <ActivateIcon />,
        onClick: this._handleActivatePipelineClicked,
        ...actionsValidationForFlow,
      },
      deleteFlow: {
        label: 'Delete Flow',
        icon: <DeleteIcon />,
        color: 'error',
        onClick: this._handleDeletePipelineClicked,
      },
    };
  }

  render() {
    const {
      data,
      flow,
      inDetailPanel,
      inPipeline,
      readOnly,
      children,
      showMenu,
      isManaged,
      showStatus,
      showTransform,
      showSend,
      showSplit,
      isDetected,
      dataSetChildren,
      disableCopying,
      disableEditMessage,
      source,
      parentDataSet,
      clipboardDatasetId,
      selectActions,
      isPausing,
      isActivating,
      isCopying,
      isDeleting,
      onEditButtonClicked,
      nodeType,
      hideMenuDetails,
      location: { pathname },
    } = this.props;
    const { isSplitDialogOpen } = this.state;

    const flowType = data?.flow_type || flow?.flow_type || flowTypes.streaming;

    // See getMenuItemDefinitions: status comes from the dataset, never the
    // upstream source, so the Nexset card's badge and menu reflect the
    // Nexset's own pause/activate state.
    const status = data.status;

    const actions = getAvailableActions({
      ...this.props,
      nodeType: nodeType || data?.node_type || 'data_set',
      flowType,
      resourceStatus: status,
      hasChildren: !!dataSetChildren?.length,
      isSingleDestinationChainFlow: isSingleDestinationChainFlow(flowType),
      isSingleChainFlow: isSingleChainFlow(flowType),
      isCapsuleCluster: isCapsuleCluster(data),
      selectActions,
      isFullScreenCanvas: isFullScreenCanvasURL(pathname),
      isMarketplaceEligible: isMarketplaceEligible(data),
    });
    const menuItemDefs = this.getMenuItemDefinitions();

    let menuItems = [];
    if (actions.menu.length > 0) {
      menuItems.push({
        label: 'Nexset',
        type: 'heading',
      });
      if (!hideMenuDetails) {
        menuItems.push(menuItemDefs.details);
      }
      actions.menu.forEach((action) => {
        menuItems.push(menuItemDefs[action]);
      });
    }

    if (actions.flow.length > 0) {
      menuItems.push({
        label: 'Flow',
        type: 'heading',
      });
      actions.flow.forEach((flowAction) => {
        menuItems.push(menuItemDefs[flowAction]);
      });
    }

    const isSharedDataset = data?.isShared;
    if (isSharedDataset) {
      menuItems = hideMenuDetails ? [] : [menuItemDefs.details];
    }

    const canShowTransform =
      actions.buttons.includes('transform') && showTransform && !isSharedDataset;
    const canSend = actions.buttons.includes('send') && showSend;
    const canSplit = actions.buttons.includes('split') && showSplit;
    const canSeeMenu = menuItems.length > 0 && showMenu && parentDataSet?.node_type !== 'splitter';

    // Keep canGenerate for UI logic
    const canGenerate =
      !(isCapsuleCluster(data) && dataSetChildren?.length) &&
      ![flowTypes.elt, flowTypes.cdc, flowTypes.replication, flowTypes.custom].includes(flowType);

    let statusClass = null,
      statusName = null,
      statusView = null;

    switch (status) {
      case Constants.SourceStatus.ACTIVE:
        statusClass = 'status-active';
        statusName = 'Active';
        break;
      case Constants.SourceStatus.PAUSED:
        statusClass = 'status-paused';
        statusName = 'Paused';
        break;
      case Constants.SourceStatus.RATE_LIMITED:
        statusClass = 'status-rate-limited';
        statusName = 'Billing Tier Limit Exceeded';
        break;
      default:
        statusClass = 'status-init';
        statusName = 'Initialized';
    }

    if (showStatus) {
      const statusImageClassName = classnames('status-image-placeholder', statusClass);
      statusView = (
        <Tooltip title={statusName} arrow={true}>
          <div className="status-holder">
            <div className={statusImageClassName}>
              <div className="bolt" />
            </div>
          </div>
        </Tooltip>
      );
    }

    const Transform = this.props.TransformComponent ? (
      React.cloneElement(this.props.TransformComponent, {
        onClick: this._handleTransformClicked,
      })
    ) : (
      <Tooltip title="Transform" arrow={true}>
        <div
          style={
            inDetailPanel
              ? styles.detailPanel.ctaContainer
              : inPipeline
                ? { ...styles.pipeline.ctaContainer }
                : styles.default.ctaContainer
          }
        >
          <TransformIcon
            data-testid="transform-dataset"
            className="dataset-tx-icon"
            onClick={this._handleTransformClicked}
            style={
              inDetailPanel
                ? styles.detailPanel.cta
                : inPipeline
                  ? styles.pipeline.cta
                  : styles.default.cta
            }
          />
        </div>
      </Tooltip>
    );

    const Send = this.props.SendComponent ? (
      React.cloneElement(this.props.SendComponent, {
        onClick: this._handleSendClicked,
      })
    ) : (
      <Tooltip title="Send to Destination" arrow={true}>
        <div
          style={
            inDetailPanel
              ? styles.detailPanel.ctaContainer
              : inPipeline
                ? styles.pipeline.ctaContainer
                : styles.default.ctaContainer
          }
          data-testid={
            inDetailPanel
              ? `send-to-destination-detail-panel-${data.id}`
              : inPipeline
                ? `send-to-destination-pipeline-${data.id}`
                : 'send-to-destination'
          }
        >
          <SendIcon
            data-testid={`send-to-destination-${data.id}`}
            onClick={this._handleSendClicked}
            className="dataset-tx-icon send-to-destination"
            style={
              inDetailPanel
                ? styles.detailPanel.cta
                : inPipeline
                  ? styles.pipeline.cta
                  : styles.default.cta
            }
          />
        </div>
      </Tooltip>
    );

    menuItems = restrictMenuForRole(menuItems, data);

    if (this.props.additionalMenuItems) {
      if (readOnly || !canSeeMenu) {
        menuItems = [];
      }
      this.props.additionalMenuItems.forEach((item) => {
        if (item.position === 'bottom') {
          menuItems.push(item);
        } else {
          menuItems.unshift(item);
        }
      });
    }

    const MenuComponent = (
      <Menu
        menuClassName={this.props.menuClassName}
        menuButtonClassName={this.props.menuButtonClassName}
        onOpen={this.props.onOpenMenu}
        onClose={this.props.onCloseMenu}
        horizontalDots={this.props.horizontalDots}
        inPipeline={inPipeline}
        items={menuItems}
      />
    );

    if (typeof children === 'function') {
      return (
        <>
          {children({
            canTransform: canShowTransform,
            canGenerate,
            canSend,
            canSplit,
            onClickSend: this._handleSendClicked,
            onClickTransform: this._handleTransformClicked,
            onClickSplit: this._handleSplitClicked,
            canSeeMenu,
            menuItems,
            statusView,
            Send,
            Transform,
            Menu: MenuComponent,
          })}
          {isSplitDialogOpen && (
            <DataSetSplitDialog parentDataSet={data} onClose={this._handleCloseSplitDialog} />
          )}
        </>
      );
    }

    return (
      <div className={classnames(s.container, this.props.className)}>
        {statusView}
        {!!canShowTransform && Transform}
        {!!canSend && Send}

        {typeof this.props.openDetailsModal === 'function' ? (
          <DetailsIconButton onClick={this.props.openDetailsModal} />
        ) : null}

        {this.props.additionalMenuItems?.length || (!readOnly && canSeeMenu) ? MenuComponent : null}
      </div>
    );
  }

  getSendPath = (flowType) => {
    const flowId = this.props.flow?.id || this.props.data.origin_node_id;
    const paths = {
      cdc: `${ROUTE_CONNECT}${ROUTE_PIPELINES}/cdc/sink/new?flowId=${flowId}&nexsetId=${this.props.data.id}`,
      in_memory: `${ROUTE_CONNECT}${ROUTE_PIPELINES}/in_memory/sink/new?flowId=${flowId}&nexsetId=${this.props.data.id}`,
      custom: `${ROUTE_CONNECT}${ROUTE_PIPELINES}/custom/sink/new?flowId=${this.props.flow?.id}&nexsetId=${this.props.data.id}`,
      replication: `${ROUTE_CONNECT}${ROUTE_PIPELINES}/replication/sink/new?flowId=${flowId}&nexsetId=${this.props.data.id}`,
      spark: `${ROUTE_CONNECT}${ROUTE_PIPELINES}/spark/sink/new?flowId=${flowId}&nexsetId=${this.props.data.id}`,
      elt: `${ROUTE_CONNECT}${ROUTE_PIPELINES}/elt/sink/new?flowId=${flowId}&nexsetId=${this.props.data.id}`,
      streaming: `${this.context}${ROUTE_MY_DATA_SETS}/${this.props.data.id}/send`,
    };
    return paths[flowType];
  };

  _handleSendClicked = (event) => {
    event?.stopPropagation();
    const { data, flow } = this.props;
    const flowId = this.props.flow?.id || this.props.data.origin_node_id;
    const flowType = data?.flow_type || flow?.flow_type;

    const isModalEnabled = editModalsEnabled()?.sink?.[flowType];

    if (isModalEnabled) {
      this.props.setAction({
        type: 'create',
        resource: RESOURCE_TYPE_SINK,
        nexsetId: data.id,
        queryParams: {
          flowId,
          nexsetId: data.id,
        },
        flowType,
        onComplete: (data) => {
          if (data?.addedDataSinkId) {
            this.props.refreshPipeline(this.props.data.origin_node_id);
          }
        },
      });
    } else {
      const path = this.getSendPath(flowType);
      if (path) {
        this.props.history.push(path);
      }
    }
  };

  _handleDeleteClicked(event) {
    const { data, isDetected, source, isPausing, isDeleting } = this.props;

    // Prevent multiple delete operations
    if (isPausing || isDeleting) {
      //Delete already in progress, ignoring duplicate request
      return;
    }

    // Validate required data
    if (!data || !data.id) {
      //console.log('Cannot delete dataset: missing data or dataset ID');
      return;
    }

    const performDelete = () => {
      this.props.deleteDataset(data.id);
      this._cleanupGenAiConversation(data.id);
    };

    // Check if dataset needs to be paused before deletion
    // Default to false if status is undefined/null
    const needsPause = data.status && [Constants.SourceStatus.ACTIVE].includes(data.status);

    if (needsPause) {
      const pausePromise = this.props.pauseDataset(data.id);

      // Wait for pause to complete, then delete
      // The UI will show "Deleting..." during the entire process (pause + delete)
      pausePromise
        .then(() => {
          performDelete();
        })
        .catch((error) => {
          //TODO: This will happen if the dataset has children. api will reject this and we ui will show a popup modal. no need to show this in console.
          //console.log(`Failed to pause dataset ${data.id}. Deletion cancelled to prevent data corruption.`);
        });
    } else {
      // Dataset is not active, delete directly
      performDelete();
    }
  }

  _cleanupGenAiConversation(datasetId) {
    const genAiChatConversation = LocalStorageUtils.getObject(`conversation_${datasetId}`);

    if (genAiChatConversation) {
      LocalStorageUtils.removeObject(`conversation_${datasetId}`);
    }
  }

  _handlePauseClicked(event) {
    // Pause acts on the Nexset only. It must not cascade to the upstream
    // source: a webhook source feeding multiple downstream Nexsets must keep
    // ingesting events when one Nexset is paused, otherwise pausing a
    // response Nexset silently drops live webhook deliveries.
    const { data } = this.props;
    this.props.pauseDataset(data.id).then(() => {
      if (data.node_type === 'splitter' && data.origin_node_id) {
        this.props.refreshPipeline(data.origin_node_id);
      }
    });
  }

  _handleActivateClicked(event) {
    const { data } = this.props;
    this.props.activateDataset(data.id).then(() => {
      if (data.node_type === 'splitter' && data.origin_node_id) {
        this.props.refreshPipeline(data.origin_node_id);
      }
    });
  }

  _handleCopyClicked(event) {
    this.props.copyDataset(this.props.data.id);
  }

  _handleCopyTransformClicked(event) {
    this.props.copyDatasetToClipboard(this.props.data.id);
  }

  _handlePasteTransformClicked(event) {
    const { clipboardDatasetId, data } = this.props;

    this.props.copyDataset(clipboardDatasetId, data.id);
  }

  _handleEditClicked = (event) => {
    const isModalEnabled = editModalsEnabled()?.nexset;
    if (isModalEnabled) {
      this.props.setAction({
        type: 'edit',
        resource: RESOURCE_TYPE_DATA_SET,
        id: this.props.data.id,
      });
    } else {
      this.props.history.push(`${this.context}${ROUTE_MY_DATA_SETS}/${this.props.data.id}/edit`);
    }
  };

  _handleTransformClicked(event) {
    event?.stopPropagation();

    const isModalEnabled = editModalsEnabled()?.nexset;
    if (isModalEnabled) {
      this.props.setAction({
        type: 'create',
        resource: RESOURCE_TYPE_DATA_SET,
        id: this.props.data.id,
        onComplete: (data) => {
          if (data?.addedDatasetId) {
            this.props.refreshPipeline(this.props.data.origin_node_id);
          }
        },
      });
    } else {
      this.props.history.push(`${this.context}${ROUTE_MY_DATA_SETS}/${this.props.data.id}/new`);
    }
  }

  _handleSplitClicked() {
    this.setState({ isSplitDialogOpen: true });
  }

  _handleCloseSplitDialog() {
    this.setState({ isSplitDialogOpen: false });
  }

  _handlePausePipelineClicked() {
    const isCapsule = isCapsuleCluster(this.props.data);
    if (isCapsule) {
      this.props.setPipelineAction(this.props.data.origin_node_id, PIPELINE_ACTION_PAUSE);
      return;
    }
    this.props.setPipelineAction(this.props.data.flow_node_id, PIPELINE_ACTION_PAUSE);
  }

  _handleActivatePipelineClicked() {
    const isCapsule = isCapsuleCluster(this.props.data);
    if (isCapsule) {
      this.props.setPipelineAction(this.props.data.origin_node_id, PIPELINE_ACTION_ACTIVATE);
      return;
    }
    this.props.setPipelineAction(this.props.data.flow_node_id, PIPELINE_ACTION_ACTIVATE);
  }

  _handleDeletePipelineClicked() {
    this.props.setPipelineAction(this.props.data.flow_node_id, PIPELINE_ACTION_DELETE);
  }

  _handleAddToMarketplace() {
    if (this.props.isMarketplaceEnabled) {
      this.props.handleOpenModal(this.props.data);
    } else {
      window.open(`${ROUTE_REDESIGN}${ROUTE_ENABLE_MARKETPLACE}`, '_blank');
    }
  }

  _handleOpenDetails(e) {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    const { data, onOpenDetailsMenu, openDetailDrawerInPortal, openDetailsModal } = this.props;
    // Use menu-only handler first (does not affect card visuals)
    if (typeof onOpenDetailsMenu === 'function') {
      onOpenDetailsMenu(data);
      return;
    }
    // Prefer parent-provided handler (list/card views) and fall back to portal drawer
    if (typeof openDetailsModal === 'function') {
      openDetailsModal(data);
      return;
    }
    if (typeof openDetailDrawerInPortal === 'function') {
      openDetailDrawerInPortal(data, {});
    }
  }
}

DataSetDetailActions.defaultProps = {
  showStatus: false,
  showTransform: true,
  showSend: true,
  showSplit: true,
  showMenu: true,
  inDetailPanel: false,
  inPipeline: false,
  isDetected: false,
  disableCopying: false,
  hideMenuDetails: false,
};

const DataSetDetailActionsWithResourceEditProvider = (props) => {
  return (
    <React.Suspense fallback={null}>
      <ResourceEditProvider>
        <ResourceEditContext.Consumer>
          {(consumerProps) => {
            return <DataSetDetailActions {...props} {...consumerProps} />;
          }}
        </ResourceEditContext.Consumer>
      </ResourceEditProvider>
    </React.Suspense>
  );
};

export default compose(withRouter, withModals)(DataSetDetailActionsWithResourceEditProvider);
